import {useState, useEffect, useCallback} from 'react';

import type {PastedImage, UploadedImage} from './types';

const uploadFileToBackend = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch('/file/upload-file', {
        method: 'POST',
        body: formData,
    });

    if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
    }

    const result = await response.json();

    return result.url;
};

interface UseImageHandlingInput {
    containerId: string;
    shouldDisablePaste: boolean;
}

export function useImageHandling(input: UseImageHandlingInput) {
    const [pastedImages, setPastedImages] = useState<PastedImage[]>([]);
    const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);

    const hasImages = pastedImages.length > 0 || uploadedImages.length > 0;

    const handlePaste = useCallback((e: ClipboardEvent) => {
        if (!e.clipboardData) return;

        const items = e.clipboardData.items;
        const newImages: PastedImage[] = [];
        let imageFound = false;

        for (let i = 0; i < items.length; i++) {
            if (items[i]!.type.indexOf('image') !== -1) {
                imageFound = true;
                const file = items[i]!.getAsFile();
                if (file) {
                    newImages.push({
                        id: `img-${Date.now()}-${i}`,
                        file,
                        previewUrl: URL.createObjectURL(file),
                    });
                }
            }
        }

        if (newImages.length > 0) {
            setPastedImages((prev) => [...prev, ...newImages]);
        }

        if (imageFound) {
            e.preventDefault();
        }
    }, []);

    const handleFilesUploaded = useCallback(({filesUploaded}) => {
        // @ts-expect-error
        const newUploadedImages: UploadedImage[] = filesUploaded.map((file, index) => ({
            id: `upload-${Date.now()}-${index}`,
            url: file.url,
        }));
        setUploadedImages((prev) => [...prev, ...newUploadedImages]);
    }, []);

    const removePastedImage = useCallback(
        (id: string) => {
            const imageToRemove = pastedImages.find((img) => img.id === id);
            if (imageToRemove) {
                URL.revokeObjectURL(imageToRemove.previewUrl);
            }
            setPastedImages(pastedImages.filter((img) => img.id !== id));
        },
        [pastedImages]
    );

    const removeUploadedImage = useCallback(
        (id: string) => {
            setUploadedImages(uploadedImages.filter((img) => img.id !== id));
        },
        [uploadedImages]
    );

    const collectAllImageUrls = useCallback(async (): Promise<string[]> => {
        const pastedImageUrls: string[] = [];
        for (const img of pastedImages) {
            try {
                const url = await uploadFileToBackend(img.file);
                pastedImageUrls.push(url);
            } catch (_error: any) {
                // TODO: Show error message to user
            }
        }

        return [...pastedImageUrls, ...uploadedImages.map((img) => img.url)];
    }, [pastedImages, uploadedImages]);

    const clearAllImages = useCallback(() => {
        pastedImages.forEach((img) => URL.revokeObjectURL(img.previewUrl));
        setPastedImages([]);
        setUploadedImages([]);
    }, [pastedImages]);

    useEffect(() => {
        const container = document.getElementById(input.containerId);
        if (container && !input.shouldDisablePaste) {
            container.addEventListener('paste', handlePaste);

            return () => {
                container.removeEventListener('paste', handlePaste);
            };
        }
    }, [input.containerId, handlePaste, input.shouldDisablePaste]);

    return {
        pastedImages,
        uploadedImages,
        hasImages,
        setPastedImages,
        setUploadedImages,
        handleFilesUploaded,
        removePastedImage,
        removeUploadedImage,
        collectAllImageUrls,
        clearAllImages,
    };
}
